/**
 * PoweredByFooter — global "Powered by iEvntür" footer.
 *
 * Rendered automatically on every public-facing route (no login required)
 * by `<GlobalPoweredByFooter />` (see below), which uses React Router's
 * `useLocation` to match a single list of public path patterns. Adding a
 * new public route to PUBLIC_PATH_MATCHERS automatically gets the footer.
 *
 * Design intent:
 *   • subtle — small text, light divider, never competing with the page
 *   • clickable — opens https://www.ievntur.com in a new tab (rel=noopener)
 *   • responsive — works equally on mobile and desktop, doesn't add layout
 *     weight on dense booking pages
 *   • accessible — visible focus ring, semantic <footer>, aria-label
 *
 * Iter-284i — global brand attribution.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';

const IEVNTUR_URL = 'https://www.ievntur.com';

// Exported so other public pages (PublicBooking.js "Powered by iEvntür"
// card, etc.) can reuse the SAME destination and never hardcode a
// duplicate URL string.
export { IEVNTUR_URL };

const PUBLIC_PATH_MATCHERS = [
  // Login / onboarding flows
  /^\/login$/,
  /^\/register$/,
  /^\/signup$/,
  /^\/sign-up$/,
  /^\/join$/,
  /^\/forgot-password$/,
  /^\/reset-password$/,
  /^\/activate$/,
  /^\/verify-contact$/,
  /^\/set-password$/,
  /^\/auth\/magic\/.*/,
  /^\/invite\/accept$/,
  // Notification footer destinations from outbound emails
  /^\/notifications\/preferences$/,
  /^\/notifications\/unsubscribe$/,
  // Public-shared talent / agency / booking surfaces
  /^\/book\/.*/,                    // public booking links
  /^\/jobs\/.*/,                    // Iter-290b — public job share landing
  /^\/c\/[^/]+$/,                   // Iter-290b Sprint E — company recruiting hub
  /^\/agency\/[^/]+$/,              // branded agency recruitment
  /^\/ambassador\/.*/,              // public talent / ambassador profile
  /^\/ambassadors$/,                // public talent discovery
  /^\/talent\/apply\/.*/,           // job application landing
];

export function isPublicPath(pathname) {
  if (!pathname) return false;
  return PUBLIC_PATH_MATCHERS.some((re) => re.test(pathname));
}

export default function PoweredByFooter() {
  return (
    <footer
      className="w-full py-4 text-center"
      role="contentinfo"
      aria-label="Powered by iEvntür"
      data-testid="powered-by-ievntur-footer"
    >
      <a
        href={IEVNTUR_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="
          inline-flex items-center gap-1.5
          text-[11px] sm:text-xs font-medium tracking-wide
          text-gray-400 hover:text-gray-600
          transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:rounded
        "
        data-testid="powered-by-ievntur-link"
      >
        <span>Powered by</span>
        <span className="font-semibold text-gray-500 hover:text-cyan-700 transition-colors">
          iEvntür
        </span>
      </a>
    </footer>
  );
}

/**
 * GlobalPoweredByFooter — drop-in for App.js.
 * Renders the footer only on public-facing routes. Authenticated dashboards
 * keep their existing layout untouched.
 */
export function GlobalPoweredByFooter() {
  const { pathname } = useLocation();
  if (!isPublicPath(pathname)) return null;
  return <PoweredByFooter />;
}
